import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import { Sparkles, MessageCircle, ArrowLeft, ArrowRight, Bot, Lightbulb, TrendingUp, Star } from 'lucide-react';
import { Button } from './ui/button';

export default function AIChatPromo({ onOpenChat }) {
  const { isRTL } = useLanguage();
  const Arrow = isRTL ? ArrowLeft : ArrowRight;

  const openChat = () => {
    // Find and click the chat FAB button
    const fab = document.querySelector('[data-testid="chat-fab-button"]');
    if (fab) fab.click();
    if (onOpenChat) onOpenChat();
  };

  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
      className="relative overflow-hidden rounded-2xl"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#0d3d35] via-[#0a4a40] to-[#0d3530]" />
      
      {/* Animated particles */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(12)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full"
            style={{
              width: 3 + (i % 4) * 2,
              height: 3 + (i % 4) * 2,
              left: `${5 + i * 8}%`,
              top: `${10 + (i % 5) * 18}%`,
              background: `rgba(45, 212, 191, ${0.08 + (i % 3) * 0.06})`
            }}
            animate={{ 
              y: [0, -15, 0], 
              x: [0, (i % 2 ? 8 : -8), 0],
              opacity: [0.1, 0.4, 0.1] 
            }}
            transition={{ repeat: Infinity, duration: 3 + i * 0.5, delay: i * 0.3 }}
          />
        ))}
      </div>

      {/* Glow effects */}
      <div className="absolute top-0 right-10 w-40 h-40 bg-primary/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-10 w-32 h-32 bg-emerald-500/8 rounded-full blur-3xl" />

      <div className="relative p-6 sm:p-8 md:p-10">
        <div className="flex flex-col md:flex-row items-center gap-6 md:gap-10">
          
          {/* Left: AI Bot illustration */}
          <div className="flex-shrink-0">
            <motion.div 
              animate={{ y: [0, -8, 0] }}
              transition={{ repeat: Infinity, duration: 3, ease: 'easeInOut' }}
              className="relative"
            >
              {/* Outer ring */}
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ repeat: Infinity, duration: 20, ease: 'linear' }}
                className="absolute -inset-3 rounded-full border border-dashed border-primary/20"
              />
              {/* Middle ring */}
              <motion.div
                animate={{ rotate: -360 }}
                transition={{ repeat: Infinity, duration: 15, ease: 'linear' }}
                className="absolute -inset-6 rounded-full border border-dashed border-primary/10"
              />
              
              {/* Orbiting dots */}
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ repeat: Infinity, duration: 8, ease: 'linear' }}
                className="absolute -inset-3"
              >
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-primary/60" />
              </motion.div>
              <motion.div
                animate={{ rotate: -360 }}
                transition={{ repeat: Infinity, duration: 6, ease: 'linear' }}
                className="absolute -inset-6"
              >
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-emerald-400/50" />
              </motion.div>

              {/* Main circle */}
              <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full bg-gradient-to-br from-primary/20 to-emerald-500/20 border border-primary/20 flex items-center justify-center backdrop-blur-sm">
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-primary to-emerald-600 flex items-center justify-center shadow-2xl shadow-primary/30">
                  <Sparkles className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                </div>
              </div>

              {/* Floating icons */}
              <motion.div
                animate={{ y: [0, -5, 0], x: [0, 3, 0] }}
                transition={{ repeat: Infinity, duration: 2, delay: 0.5 }}
                className="absolute -top-2 -right-3 w-9 h-9 rounded-xl bg-amber-500/20 border border-amber-500/30 flex items-center justify-center"
              >
                <Lightbulb className="w-4 h-4 text-amber-400" />
              </motion.div>
              <motion.div
                animate={{ y: [0, 5, 0], x: [0, -3, 0] }}
                transition={{ repeat: Infinity, duration: 2.5, delay: 1 }}
                className="absolute -bottom-1 -left-3 w-9 h-9 rounded-xl bg-blue-500/20 border border-blue-500/30 flex items-center justify-center"
              >
                <TrendingUp className="w-4 h-4 text-blue-400" />
              </motion.div>
            </motion.div>
          </div>

          {/* Right: Text content */}
          <div className="flex-1 text-center md:text-right">
            <div className="flex items-center justify-center md:justify-end gap-2 mb-3">
              <span className="bg-primary/20 text-primary text-[10px] font-semibold px-2.5 py-1 rounded-full border border-primary/20 flex items-center gap-1">
                <Bot className="w-3 h-3" />
                AI Assistant
              </span>
              <span className="bg-emerald-500/15 text-emerald-400 text-[10px] font-semibold px-2.5 py-1 rounded-full border border-emerald-500/20">
                GPT-5.2
              </span>
            </div>

            <h3 className="text-xl sm:text-2xl font-bold text-white leading-relaxed mb-3">
              {isRTL 
                ? 'مش عارف من وين تبدأ؟ 🤔' 
                : "Not sure where to start? 🤔"
              }
            </h3>
            
            <p className="text-[15px] text-white/70 leading-relaxed mb-2">
              {isRTL
                ? 'مندوبنا الذكي موجود لمساعدتك! اسأله عن أفضل المنتجات للبيع، كيف تبدأ، أو أي سؤال عندك.'
                : "Our AI assistant is here to help! Ask about the best products to sell, how to start, or any question you have."
              }
            </p>
            
            <p className="text-[14px] text-white/50 leading-relaxed mb-5">
              {isRTL
                ? 'خلينا نساعدك تكبر وتنجح معنا — خطوة بخطوة 🚀'
                : "Let us help you grow and succeed — step by step 🚀"
              }
            </p>

            {/* Features mini cards */}
            <div className="flex flex-wrap items-center justify-center md:justify-end gap-2 mb-5">
              {[
                { icon: Star, text: isRTL ? 'أفضل المنتجات للبيع' : 'Best products to sell' },
                { icon: MessageCircle, text: isRTL ? 'إجابة فورية 24/7' : 'Instant answers 24/7' },
                { icon: TrendingUp, text: isRTL ? 'نصائح لزيادة أرباحك' : 'Tips to boost earnings' },
              ].map((f, i) => (
                <span key={i} className="flex items-center gap-1.5 bg-white/5 border border-white/10 px-3 py-1.5 rounded-lg text-[11px] text-white/60">
                  <f.icon className="w-3 h-3 text-primary" />
                  {f.text}
                </span>
              ))}
            </div>

            <Button 
              onClick={openChat}
              className="gap-2.5 bg-white text-gray-900 hover:bg-white/90 rounded-xl h-12 px-6 text-sm font-bold shadow-xl shadow-black/20"
            >
              <MessageCircle className="w-5 h-5" />
              {isRTL ? 'تحدث مع المساعد الذكي' : 'Chat with AI Assistant'}
              <Arrow className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </motion.section>
  );
}
